/**
 * Fetch-Hülle für die BauSepp-Schnittstelle.
 *
 * Aufgaben, die hier gebündelt sind, damit kein Aufrufer sie wiederholen muss:
 * Token mitschicken, abgelaufenen Zugangstoken einmal erneuern, Korrelations-ID
 * setzen, Fehlerform des Backends in eine Ausnahme übersetzen.
 */
import type { Json, RefreshIn, TokenOut } from "./types";

const API_BASE = import.meta.env.VITE_API_BASE ?? "/api/v1";

const STORAGE_KEY = "bausepp.session";

export interface StoredSession {
  accessToken: string;
  refreshToken: string;
  /** Ablauf des Zugangstokens als Zeitstempel in Millisekunden. */
  expiresAt: number;
  organizationId: string | null;
  organizationName: string | null;
  /** Zweiter Faktor ist für die Rolle Pflicht, aber noch nicht eingerichtet. */
  mfaSetupRequired: boolean;
}

// ---------------------------------------------------------------------------
// Sitzungsspeicher
// ---------------------------------------------------------------------------

/*
 * Die Token liegen in localStorage. Bewusste Abwägung: Ein httpOnly-Cookie wäre
 * gegen Skriptangriffe besser geschützt, aber das Backend gibt die Token im
 * Antwortkörper aus (siehe backend/app/api/v1/auth.py) und die Anwendung muss
 * eine Anmeldung über einen Neustart hinweg behalten — ohne das wäre ein
 * Rundgang nach jedem Wischen aus dem Aufgabenwechsler beendet. Gegenmaßnahme
 * gegen die verbleibende Gefahr ist die strenge Inhaltssicherheitsrichtlinie
 * des Backends, nicht ein anderer Speicherort.
 */
let session: StoredSession | null = readSession();
const listeners = new Set<(next: StoredSession | null) => void>();

function readSession(): StoredSession | null {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed: unknown = JSON.parse(raw);
    if (
      typeof parsed === "object" &&
      parsed !== null &&
      "accessToken" in parsed &&
      "refreshToken" in parsed
    ) {
      return parsed as StoredSession;
    }
    return null;
  } catch {
    // Beschädigter Eintrag darf den Start nicht verhindern.
    return null;
  }
}

export function getSession(): StoredSession | null {
  return session;
}

export function isAuthenticated(): boolean {
  return session !== null;
}

export function setSession(next: StoredSession | null): void {
  session = next;
  try {
    if (next) localStorage.setItem(STORAGE_KEY, JSON.stringify(next));
    else localStorage.removeItem(STORAGE_KEY);
  } catch {
    // Privater Modus ohne Speicher: Die Sitzung gilt dann nur für dieses Fenster.
  }
  for (const listener of listeners) listener(next);
}

export function subscribeSession(listener: (next: StoredSession | null) => void): () => void {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function sessionFromToken(token: TokenOut): StoredSession {
  return {
    accessToken: token.access_token,
    refreshToken: token.refresh_token,
    expiresAt: Date.now() + token.expires_in * 1000,
    organizationId: token.organization?.id ?? null,
    organizationName: token.organization?.name ?? null,
    mfaSetupRequired: token.mfa_setup_required,
  };
}

// ---------------------------------------------------------------------------
// Fehler
// ---------------------------------------------------------------------------

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: Json = {},
    readonly correlationId: string | null = null,
  ) {
    super(message);
    this.name = "ApiError";
  }

  /** Aufruf hat den Dienst nie erreicht — Netz weg, Flugmodus, Keller. */
  get isOffline(): boolean {
    return this.status === 0;
  }

  /**
   * Der Endpunkt existiert im Backend noch nicht. Für die Warteschlange
   * entscheidend: Solche Vorgänge dürfen nicht endlos wiederholt werden.
   */
  get isUnimplemented(): boolean {
    return this.status === 404 || this.status === 405;
  }
}

interface BackendErrorBody {
  error?: {
    code?: string;
    message?: string;
    details?: Json;
    correlation_id?: string | null;
  };
}

// ---------------------------------------------------------------------------
// Anfragen
// ---------------------------------------------------------------------------

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  /**
   * JSON-Nutzlast — oder `FormData` für Dateien. Bei `FormData` bleibt der
   * Content-Type ungesetzt, damit der Browser die Trennmarke ergänzt.
   */
  body?: unknown;
  /** Wird als `Idempotency-Key` gesendet; Pflicht für Vorgänge aus der Warteschlange. */
  idempotencyKey?: string;
  signal?: AbortSignal;
  /** Auf `false` setzen für Anmeldung und Einmal-Link. */
  auth?: boolean;
  query?: Record<string, string | number | boolean | undefined | null>;
}

function buildUrl(path: string, query: RequestOptions["query"]): string {
  const url = `${API_BASE}${path}`;
  if (!query) return url;
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null || value === "") continue;
    search.set(key, String(value));
  }
  const qs = search.toString();
  return qs ? `${url}?${qs}` : url;
}

/*
 * Erneuerung läuft einfach durch: Laufen fünf Abfragen parallel und bekommen
 * alle 401, soll nur eine den Aktualisierungstoken einlösen. Das Backend
 * beendet bei doppelt eingelöstem Token alle Sitzungen (siehe auth.py) — ohne
 * diese Sperre würde die Anwendung sich also selbst abmelden.
 */
let refreshInFlight: Promise<StoredSession | null> | null = null;

async function refreshSession(): Promise<StoredSession | null> {
  const current = session;
  if (!current) return null;

  refreshInFlight ??= (async () => {
    try {
      const payload: RefreshIn = { refresh_token: current.refreshToken };
      const token = await rawRequest<TokenOut>("/auth/refresh", {
        method: "POST",
        body: payload,
        auth: false,
      });
      const next = sessionFromToken(token);
      // Organisation kommt bei der Erneuerung nicht mit; den bekannten Stand behalten.
      next.organizationId = current.organizationId;
      next.organizationName = current.organizationName;
      setSession(next);
      return next;
    } catch {
      setSession(null);
      return null;
    } finally {
      refreshInFlight = null;
    }
  })();

  return refreshInFlight;
}

async function rawRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const { method = "GET", body, idempotencyKey, signal, auth = true, query } = options;

  const headers = new Headers({ Accept: "application/json" });
  // Eigene Korrelations-ID je Anfrage: Damit lässt sich eine Meldung vom
  // Telefon bis in das Serverprotokoll verfolgen. Das Backend erwartet eine
  // UUID und würde alles andere stillschweigend ersetzen.
  headers.set("X-Correlation-ID", crypto.randomUUID());
  // Bei FormData **kein** Content-Type setzen: Der Browser ergänzt ihn mit der
  // Trennmarke (`boundary`), die der Server zum Zerlegen braucht. Setzt man ihn
  // selbst, fehlt sie, und der Server lehnt mit 400 ab — ohne zu sagen, warum.
  const istFormular = typeof FormData !== "undefined" && body instanceof FormData;
  if (body !== undefined && !istFormular) headers.set("Content-Type", "application/json");
  if (idempotencyKey) headers.set("Idempotency-Key", idempotencyKey);
  if (auth && session) headers.set("Authorization", `Bearer ${session.accessToken}`);

  let response: Response;
  try {
    response = await fetch(buildUrl(path, query), {
      method,
      headers,
      body: body === undefined ? undefined : istFormular ? (body as FormData) : JSON.stringify(body),
      ...(signal ? { signal } : {}),
    });
  } catch (cause) {
    if (cause instanceof DOMException && cause.name === "AbortError") throw cause;
    throw new ApiError(0, "offline", "Keine Verbindung zum Server", {
      cause: String(cause),
    });
  }

  if (response.status === 204) return undefined as T;

  const text = await response.text();
  const parsed: unknown = text ? safeJson(text) : null;

  if (!response.ok) {
    const err = (parsed ?? {}) as BackendErrorBody;
    throw new ApiError(
      response.status,
      err.error?.code ?? `http_${response.status}`,
      err.error?.message ?? (response.statusText || "Unbekannter Fehler"),
      err.error?.details ?? {},
      err.error?.correlation_id ?? response.headers.get("X-Correlation-ID"),
    );
  }

  return parsed as T;
}

function safeJson(text: string): unknown {
  try {
    return JSON.parse(text);
  } catch {
    return null;
  }
}

/**
 * Ein Aufruf gegen die Schnittstelle. Bei abgelaufenem Zugangstoken wird genau
 * einmal erneuert und wiederholt; scheitert das, ist die Sitzung beendet und die
 * Anwendung führt zur Anmeldung.
 */
export async function request<T>(path: string, options: RequestOptions = {}): Promise<T> {
  try {
    return await rawRequest<T>(path, options);
  } catch (error) {
    const retryable =
      error instanceof ApiError && error.status === 401 && (options.auth ?? true) && session !== null;
    if (!retryable) throw error;

    const renewed = await refreshSession();
    if (!renewed) throw error;
    return rawRequest<T>(path, options);
  }
}

export { API_BASE };
